import { GitMerge } from '@untitledui/icons';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUser } from '@/allauth_auth/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import type { ResponsiveAction } from '@/lib/base/buttons';
import type { CrmObject, EntityTypeName } from '@/lib/crm/core/types';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { buildEntityKindMap } from '@/lib/crm/utils/associationMatching';

import { getEntityTypeConfig } from '../../record/registry/entityTypeRegistry';
import { getMergeCandidateSearchTypes } from './mergeEligibility';
import { MergeRecordPickerDialog } from './MergeRecordPickerDialog';
import { MergeWizard } from './MergeWizard';

export interface UseMergeRecordActionArgs {
  object: CrmObject;
  entityTypeName: EntityTypeName;
  viewContext: 'slideout' | 'fullPage';
  listRoute?: string;
}

export interface UseMergeRecordActionResult {
  mergeAction: ResponsiveAction | null;
  mergeElements: ReactElement;
}

export function useMergeRecordAction({
  object,
  entityTypeName,
  viewContext,
  listRoute,
}: UseMergeRecordActionArgs): UseMergeRecordActionResult {
  const navigate = useNavigate();
  const { replaceTop } = useSlideoutStack();
  const { hasPermission } = usePermissions();
  const user = useUser();
  const { data: objectTypesData } = useObjectTypes(object.org_id);

  const entityKindsMap = useMemo(
    () => buildEntityKindMap(objectTypesData?.allObjectTypes ?? []),
    [objectTypesData?.allObjectTypes]
  );
  const searchTypes = useMemo(
    () => getMergeCandidateSearchTypes(entityTypeName, entityKindsMap),
    [entityTypeName, entityKindsMap]
  );

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [pickedEntityId, setPickedEntityId] = useState<string | null>(null);

  const showMerge =
    user?.id !== undefined &&
    hasPermission(entityTypeName, 'ahoy_object_merged') &&
    searchTypes.length > 0;

  const openPicker = useCallback(() => {
    setIsPickerOpen(true);
  }, []);

  const handlePick = useCallback((entityId: string) => {
    setPickedEntityId(entityId);
    setIsPickerOpen(false);
    setIsWizardOpen(true);
  }, []);

  const handleWizardOpenChange = useCallback((open: boolean) => {
    setIsWizardOpen(open);
    if (!open) {
      setPickedEntityId(null);
    }
  }, []);

  const handleMergeComplete = useCallback(
    (canonicalEntityId: string) => {
      if (viewContext === 'slideout') {
        if (canonicalEntityId !== object.entity_id) {
          replaceTop(canonicalEntityId);
        }
        return;
      }
      const config = getEntityTypeConfig(entityTypeName);
      const routePattern = config.fullPageRoute;
      if (routePattern) {
        navigate(
          routePattern
            .replace(':orgId', String(object.org_id))
            .replace(':recordId', canonicalEntityId)
        );
      } else if (listRoute) {
        navigate(listRoute.replace(':orgId', String(object.org_id)));
      }
    },
    [
      viewContext,
      object.entity_id,
      object.org_id,
      replaceTop,
      entityTypeName,
      listRoute,
      navigate,
    ]
  );

  const mergeAction = useMemo<ResponsiveAction | null>(
    () =>
      showMerge
        ? {
            key: 'merge',
            label: 'Merge records',
            icon: GitMerge,
            onClick: openPicker,
            placement: 'dropdown',
          }
        : null,
    [showMerge, openPicker]
  );

  const mergeElements = useMemo<ReactElement>(
    () => (
      <>
        <MergeRecordPickerDialog
          isOpen={isPickerOpen}
          onOpenChange={setIsPickerOpen}
          orgId={object.org_id}
          searchTypes={searchTypes}
          currentEntityId={object.entity_id}
          onPick={handlePick}
        />
        {pickedEntityId && (
          <MergeWizard
            isOpen={isWizardOpen}
            onOpenChange={handleWizardOpenChange}
            orgId={object.org_id}
            entityTypeName={entityTypeName}
            entityIds={[object.entity_id, pickedEntityId]}
            onMergeComplete={handleMergeComplete}
          />
        )}
      </>
    ),
    [
      isPickerOpen,
      object.org_id,
      object.entity_id,
      searchTypes,
      handlePick,
      pickedEntityId,
      isWizardOpen,
      handleWizardOpenChange,
      entityTypeName,
      handleMergeComplete,
    ]
  );

  return { mergeAction, mergeElements };
}
